import { i18n } from '@lingui/core';
import type { SearchKey } from '@schibsted-nmp/search-frontend-clients/quest';
import { LegendaryRealestateSearchAd } from '@schibsted-nmp/search-frontend-components/react/legendary';
import React from 'react';
import type {
    PolePositionEntry,
    RealestatePolePositionEntry,
    RealestateProjectPolePositionEntry,
} from '../../../server';
import { FavoriteHeart } from '../FavoriteHeart';
import { useDesktopBreakpoint } from './useDesktopBreakpoint';

const FALLBACK_IMAGE_WIDTH = 640;

const BASIC_IMAGE_SIZES =
    '(min-width: 1300px) 210px, (min-width: 1025px) 215px, (min-width: 768px) 22vw, 33vw';

const PLUSS_IMAGE_SIZES =
    '(min-width: 1300px) 310px, (min-width: 1025px) 320px, (min-width: 768px) 33vw, 50vw';

const PREMIUM_IMAGE_SIZES =
    '(min-width: 1300px) 625px, (min-width: 1025px) 645px, (min-width: 768px) 33vw, 100vw';

type RealestatePolePositionAdProps = {
    ad: RealestatePolePositionEntry | RealestateProjectPolePositionEntry;
    onClick: () => void;
    promotedLabel?: string;
    renderTime?: number;
    searchKey: SearchKey;
};

export function RealestatePolePositionAd(props: RealestatePolePositionAdProps) {
    const { ad, onClick, promotedLabel, renderTime, searchKey } = props;

    const adProps = {
        currentTime: renderTime,
        onClick,
        searchKey,
        url: ad.canonical_url,
    };

    const isLettings = searchKey === 'SEARCH_ID_REALESTATE_LETTINGS';
    const format = getFormat(ad, isLettings);
    const isDesktop = useDesktopBreakpoint();

    const isMobileBasicAd = !isDesktop && format === 'basic';

    const labelText =
        promotedLabel ??
        i18n.t({
            id: 'pole-position-ad.promoted-badge',
            message: 'Promoted ad',
        });

    return (
        <LegendaryRealestateSearchAd
            ad={ad}
            basicImageSizes={BASIC_IMAGE_SIZES}
            plussImageSizes={PLUSS_IMAGE_SIZES}
            premiumImageSizes={PREMIUM_IMAGE_SIZES}
            secondaryImageWidth={FALLBACK_IMAGE_WIDTH}
            favorite={() => (
                <FavoriteHeart
                    itemId={ad.id}
                    variant={isMobileBasicAd ? 'heart' : 'heart-on-image'}
                />
            )}
            label={({ className, classes }) => (
                <span
                    className={`${className} ${classes.badge} ${classes.info}`}
                >
                    {labelText}
                </span>
            )}
            {...adProps}
        />
    );
}

function getFormat(
    ad: PolePositionEntry,
    isLettings = false,
): 'premium' | 'pluss' | 'basic' {
    if (ad.flags?.includes('highlight_listing_estate_premium')) {
        return 'premium';
    }
    if (ad.flags?.includes('highlight_listing_estate_pluss') || isLettings) {
        return 'pluss';
    }
    return 'basic';
}
